import { Box,  CircularProgress } from "@mui/material";
import { DataGrid, GridCellParams, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { JobSummary } from "../../../../api/models/schema";
import { useApi } from "../../../../hooks/useApi";
import { downloadJobAPI } from "../../../../api/liveMarketingApi";

interface RowItem {
  jobId: number;
  jobNumber: string;
  jobName: string;
  mailDate: Date;
  numRecordsToMail: number;
  numRecordsScrubbed: number;
  originalFileName: string;
  accuZipGuid: string;
  accuZipFileName: string;
  zipMailFileName: string;
  productName: string;
  dateCreated: Date;
}

export interface JobGridProps {
  jobs: JobSummary[];
}
export enum FileType {
  HTML = 1,
  CSV,
  PDF,
  EMAIL,
  TXT,
  SMS,
  ZIP,
  XML,
  RTF,
  JPG,
}

export const FileTypeContent = new Map<number, string>([
  [FileType.PDF, 'application/pdf'],
  [FileType.CSV, 'text/csv'],
  [FileType.HTML, 'text/html'],
  [FileType.RTF, 'text/rtf'],
  [FileType.TXT, 'text/txt'],
  [FileType.XML, 'text/xml'],
  [FileType.ZIP, 'application/zip'],
]);

export interface BlobFile {
  filename: string;
  blob: string; 
  fileType: FileType;
}

export function JobGrid(props: JobGridProps) {
  const [jobRows, setJobRows] = useState<RowItem[]>([]);
  const downloadRequest = useApi(downloadJobAPI)

  const columns: GridColDef[] = [
    {
      field: "dateCreated",
      headerName: "Date Created",
      width: 110,
      renderCell: renderDate,
    },
    { field: "jobNumber", headerName: "Job Number", width: 120 },
    { field: "jobName", headerName: "Job Name", width: 150 },
    {
      field: "mailDate",
      headerName: "Mailing Date",
      // width: 100,
      renderCell: renderDate,
    },
    {
      field: "numRecordsToMail",
      headerName: "Num Records",
    },
    {
      field: "numRecordsScrubbed",
      headerName: "Records Scrubbed",
      width: 135,
    },
    // { field: "productName", headerName: "Product", width: 150 },
    { field: "state", headerName: "State", width: 250 },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: renderStatus,
    },
    {
      field: "download",
      headerName: "Download",
      renderCell: renderDownLoad
    },
  ];

  function renderStatus(params: GridCellParams) {
    const display =
    
      params.value === "processing" ? (
        <span>
          <CircularProgress size={9} /> {params.value}
        </span>
      ) : (
        <span>{params.value}</span>
      );
    return display;
  }

  function renderDownLoad(params: GridCellParams) { 
    const row = params.row;
    const display =
        row.status === "complete" ? (
        <a href="#" onClick={() => downloadJob(row.download)} >download</a>
      ) : (
        ''
      );
    return display;
   
  }

  function renderDate(params: GridCellParams) {
    const dateString:string = params.value;
    const date = dayjs(dateString).format("MM/DD/YY");

    return <span>{date}</span>;
  }

  function downloadJob(jobNumber: number) {
    localStorage.setItem('jobNumber', `${jobNumber}`);
    downloadRequest.request(jobNumber);
  }

  useEffect(() => {
          if (downloadRequest.data) {
           const jobNumber = localStorage.getItem('jobNumber');
           const blobFile = {
              blob: downloadRequest.data,
              fileType:  FileType.ZIP,
              filename: `${jobNumber} - job download`
            } as BlobFile
              downloadBlob(blobFile);
          }
  }, [downloadRequest.data]);
  
  useEffect(() => {
    const mapRows: RowItem[] = props.jobs
      .map((row) => {
        const dateCreated = dayjs(row.dateCreated);
        const today = dayjs(new Date());
        if (
          row.status != "complete" ||
          (row.status == "complete" &&
            Math.abs(dateCreated.diff(today, "day")) < 30)
        ) {
          return {
            jobId: row.jobId!,
            jobNumber: row.jobNumber,
            jobName: row.jobName,
            mailDate: row.mailDate,
            productName: row.name,
            status: row.status,
            state: row.state,
            numRecordsToMail: row.numRecordsToMail,
            numRecordsScrubbed: row.numRecordsScrubbed,
            originalFileName: row.hasOriginalFile ? "original" : "",
            scrubFileName: row.hasScrubFile ? "scrub" : "",
            accuZipGuid: row.accuZipGuid ?? "",
            accuZipFileName: row.hasAccuZipFile ? "accuZip" : "",
            zipMailFileName: row.hasZipMailFile ? "zipMail" : "",
            dateCreated: row.dateCreated,
            download: row.jobNumber
          } as RowItem;
        } else {
          return null;
        }
      })
      .filter((r): r is RowItem => r !== null);

    const orderedRows = mapRows.sort((a, b) => {
      if (a.dateCreated < b.dateCreated) {
        return 1;
      }
      if (a.dateCreated > b.dateCreated) {
        return -1;
      }
      return 0;
    });

    setJobRows(orderedRows);
  }, [props]);

  return (
    <Box>
      <DataGrid
        autoHeight
        rows={jobRows}
        density="compact"
        disableSelectionOnClick
        rowsPerPageOptions={[20]}
        pageSize={20}
        columns={columns}
        style={{ backgroundColor: "#FFFFFF" }}
        getRowId={(row) => row.jobId}
      />
    </Box>
  );

  function downloadBlob(blob: BlobFile) {
    const fileTarget = getBlobFileTarget(blob);
    fileTarget.download = blob.filename;

    fileTarget.click();
  }

  function getBlobFileTarget(blob: BlobFile) {
    const blobData = blob.fileType === FileType.PDF || blob.fileType === FileType.ZIP
        ? Uint8Array.from(atob(blob.blob).split('').map(char => char.charCodeAt(0)))
        : atob(blob.blob);
    const file = new Blob([blobData], { type: FileTypeContent.get(blob.fileType) });
    const fileTarget = document.createElement('a');
    fileTarget.href = URL.createObjectURL(file);
    return fileTarget;
  }
}
